import { createClient } from '@supabase/supabase-js'

import { env } from './env'
import type { Database } from '@/types/database'

/**
 * The one Supabase client. Typed against the generated schema, so a renamed
 * column is a build error rather than an undefined at a venue.
 *
 * There is deliberately no second client and no service-role client anywhere in
 * `src`. Anything needing elevated rights is an edge function.
 */
export const supabase = createClient<Database>(env.VITE_SUPABASE_URL, env.VITE_SUPABASE_PUBLISHABLE_KEY, {
  auth: {
    persistSession: true,
    autoRefreshToken: true,
    // The magic-link and recovery flows land back on the app with a code in the
    // URL; PKCE is what makes that exchange safe in a browser.
    flowType: 'pkce',
    detectSessionInUrl: true,
    storageKey: 'deleeuw-studio-auth',
  },
  global: {
    headers: { 'x-application-name': 'deleeuw-studio' },
  },
})

export type Tables = Database['public']['Tables']
export type Enums = Database['public']['Enums']

/** A row as it comes back from a select. */
export type Row<T extends keyof Tables> = Tables[T]['Row']
/** The shape an insert accepts — optional where the database has a default. */
export type Insert<T extends keyof Tables> = Tables[T]['Insert']
/** The shape an update accepts — every field optional. */
export type Update<T extends keyof Tables> = Tables[T]['Update']

/**
 * Turns a PostgREST error into something worth showing a human.
 *
 * Two failures dominate this project and both look like nothing at the call
 * site, so they are named explicitly here rather than left as a shrug:
 *   * an empty array because a grant or a policy is missing
 *   * a 401 because the session expired while a laptop was shut
 */
export function describeError(error: unknown): string {
  if (!error) return 'Something went wrong.'

  if (typeof error === 'object' && 'code' in error && 'message' in error) {
    const { code, message } = error as { code: string; message: string; details?: string }

    switch (code) {
      case 'PGRST301':
      case '42501':
        return 'You do not have permission to do that. If that seems wrong, sign out and back in.'
      case '23505':
        return 'That already exists.'
      case '23503':
        return 'Something this depends on is missing or has been removed.'
      case '23514':
        return 'Those values are not allowed together. Check the highlighted fields.'
      case 'PGRST116':
        return 'That record no longer exists.'
      default:
        return message
    }
  }

  if (error instanceof Error) return error.message
  return String(error)
}
